import { useEffect, useRef, useState } from "react";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import Autocomplete from "react-google-autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { HelpCircle, MessageSquare } from "lucide-react";

import { Label } from "../../common/customDesignedComponents/label";
import { GOOGLE_API_KEY, USA_STATES } from "../../helpers/constants";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import { FormInput } from "../../common/customDesignedComponents/FormInput";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import StateDropdown from "../../common/customDesignedComponents/StateDropdown";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";
import {
  buildPropertySearchAddress,
  decryptPayload,
  getObjLength,
  getSignupUserId,
  mergeGenerateQuestionsPayload,
  normalizePropertySearchPayload,
  normalizeUsStateValue,
  persistPropertySearchAndBillingPrefillFromReport,
  persistSignupMonitoringPropertyIds,
  scrollToFirstFormikError,
} from "../../helpers/utils";
import { setPropertySearchModeFromPayload } from "../../helpers/enhancedMonitoringSearchRules";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../common/customDesignedComponents/dialog";
import {
  SEARCH_PROPERTY_ADDRESS_FORMAT,
  TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS,
} from "../../helpers/constants";
import PropertySearchErrorMessage from "../PropertySearchErrorMessage";

const validationSchema = Yup.object().shape({
  street_number: Yup.string()
    .max(15, "Too Long!")
    .required("Street number is required"),
  site_route: Yup.string().required("Street Name is required"),
  city: Yup.string()
    .strict(true)
    .max(50, "Too Long!")
    .required("City is required"),
  state: Yup.string().required("State is required"),
  zip: Yup.string()
    .required("Zip is required")
    .matches(/^[0-9]{5}$/, "Zipcode must be exactly 5 digits"),
  unit_no: Yup.string(),
});

interface FindPropertyByAddressV2Props {
  onBtnClick?: () => void;
  handleChange?: (place: any) => void;
  propertyValues?: any;
  setPropertyValues?: (values: any) => void;
  otherPropertyValues?: any;
  handleToggle?: () => void;
  gotoQuestions?: (data: any) => void;
  addExistingAddress?: () => void;
  hasSearched?: boolean;
  searchedAddress?: string;
  setSearchedAddress?: (value: string) => void;
  isPropertyConfirmed?: boolean | null;
  propertyQuestion?: any;
  category?: string;
  agreementCategory?: string;
  isFromRiskReport?: boolean;
  isByPassOptionAvailable?: boolean;
  isAdminCreateUser?: boolean;
  isServiceProvider?: boolean;
  isAssistant?: boolean;
  addPropertyError?: string;
  setAddPropertyError?: (msg: string) => void;
  questionsErrMsg?: string;
  errMsg?: string;
}

const FindPropertyByAddressV2 = ({
  onBtnClick,
  handleChange,
  propertyValues,
  setPropertyValues = (values: any) => {},
  otherPropertyValues,
  handleToggle,
  gotoQuestions,
  addExistingAddress,
  hasSearched = false,
  searchedAddress,
  setSearchedAddress,
  isPropertyConfirmed,
  propertyQuestion,
  category,
  agreementCategory,
  isFromRiskReport = false,
  isByPassOptionAvailable = false,
  isAdminCreateUser = false,
  isServiceProvider = false,
  isAssistant = false,
  addPropertyError = "",
  setAddPropertyError = (_msg: string) => {},
  questionsErrMsg = "",
  errMsg = "",
}: FindPropertyByAddressV2Props) => {
  const dispatch = useDispatch();

  const { isQuestionByPass } = useSelector(
    (state: any) => state?.properties,
  );
  // SP client add/create (/clients/*): auto-bypass — hide the checkbox.
  // SP personal (/servicer-properties) and Admin still show it.
  const showBypassCheckbox =
    isByPassOptionAvailable &&
    !isAssistant &&
    !(isServiceProvider && isAdminCreateUser);

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  // When isFromRiskReport, suppress the modal until the bind-risk-report API completes
  const [showAddressModal, setShowAddressModal] = useState(
    isFromRiskReport
      ? false
      : Boolean(localStorage.getItem("searched_Add_popup")),
  );
  const { genearteQuesPayload } = useSelector(
    (state: any) => state?.properties,
  );
  const {
    bindRiskReportLoader,
    selectedProperty,
    createAccResponse,
    user_id,
    statesCodeList,
  } = useSelector((state: any) => state?.signUpUser);
  // Track previous loader value to detect true→false transition (API just finished)
  const prevBindLoaderRef = useRef(false);

  const isMonitoringCategory =
    category === "enhanced_monitoring" || category === "basic_monitoring";

  const resolvedCategory =
    agreementCategory ||
    category ||
    decryptPayload(localStorage.getItem("category")) ||
    "complete_protection";
  const isCompleteProtection = resolvedCategory === "complete_protection";

  const initialValues = {
    street_number: propertyValues?.street_number || "",
    site_route: propertyValues?.site_route || "",
    city: propertyValues?.city || "",
    state:
      USA_STATES?.find(
        (state) =>
          state?.code === propertyValues?.state ||
          state?.name?.toLowerCase() === propertyValues?.state?.toLowerCase(),
      )?.code || "",
    zip: propertyValues?.zip || "",
    unit_no: propertyValues?.unit_no || "",
    county: propertyValues?.county || "",
  };

  const handleUseExistingAddress = () => {
    setShowAddressModal(false);
    localStorage.removeItem("searched_Add_popup");
    addExistingAddress();
  };

  const handleEnterNewAddress = () => {
    setShowAddressModal(false);
    localStorage.removeItem("searched_Add_popup");
    setPropertyValues({});
  };

  const goToNextStep = () => {
    // First check if terms and conditions are agreed (Complete Protection only)
    if (isCompleteProtection && !agreeToTerms) {
      setTermsError("You must agree to the security terms to continue");
      return;
    }

    // Clear terms error if checkbox is checked
    setTermsError("");
    if (isMonitoringCategory) {
      // Admin/SP create-client Find Property already runs create / add-property
      // when the county record is selected (PropertySearchType). Do not fire a
      // second properties/add-property-in-report from Next — that stacked calls
      // and looped after failed retries.
      if (isAdminCreateUser) {
        if (propertyQuestion?.success) {
          handleNextStepAction();
        }
        return;
      }

      if (propertyQuestion?.success) {
        handleNextStepAction();
      }
      return;
    } else {
      handleNextStepAction();
    }
  };
  const handleNextStepAction = () => {
    dispatch(userSigupAction.showSecurityInfoScreen(true));
    dispatch(propertiesAction.showSecurityInfoScreen(true));
    if (getObjLength(propertyQuestion?.data)) {
      persistPropertySearchAndBillingPrefillFromReport(propertyQuestion.data, {
        selectedProperty,
      });
    }

    if (propertyQuestion?.data) {
      persistSignupMonitoringPropertyIds(
        dispatch,
        userSigupAction,
        propertyQuestion.data,
      );
    }

    onBtnClick();
    localStorage.setItem("button", "back");
  };

  useEffect(() => {
    if (
      isMonitoringCategory &&
      getObjLength(propertyQuestion) &&
      propertyQuestion?.success
    ) {
      handleNextStepAction();
    }
  }, [propertyQuestion]);

  // Show the address modal once the bind-risk-report API finishes (loader: true → false)
  useEffect(() => {
    if (isFromRiskReport) {
      if (prevBindLoaderRef.current && !bindRiskReportLoader) {
        setShowAddressModal(
          Boolean(localStorage.getItem("searched_Add_popup")),
        );
      }
      prevBindLoaderRef.current = bindRiskReportLoader;
    }
  }, [isFromRiskReport, bindRiskReportLoader]);

  return (
    <>
      <div className="space-y-2 mb-2">
        <Autocomplete
          className="googleAutoComplete mw-100"
          name="searched_address"
          id="propertyAddress"
          value={searchedAddress}
          apiKey={GOOGLE_API_KEY}
          onChange={(e: any) => {
            console.log("add", e);
            setSearchedAddress(e.target.value);
          }}
          onPlaceSelected={(place) => {
            setSearchedAddress(place?.formatted_address);
            if (typeof handleChange === "function") {
              handleChange(place);
            }
          }}
          options={{
            types: ["address"],
            componentRestrictions: { country: "us" },
          }}
          placeholder={SEARCH_PROPERTY_ADDRESS_FORMAT}
        />
      </div>

      {/* Address Form */}
      <Formik
        enableReinitialize
        validateOnChange
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (isCompleteProtection && !agreeToTerms) {
            setTermsError("You must agree to the security terms to continue");
            return;
          }
          setTermsError("");
          setAddPropertyError("");

          // Validate other form fields (Formik handles this automatically)
          if (values.unit_no?.length === 0) {
            delete values?.unit_no;
          }

          const data = normalizePropertySearchPayload(
            Object.assign(values, otherPropertyValues, {
              state: normalizeUsStateValue(values.state) || values.state || "",
            }),
          );
          setPropertySearchModeFromPayload(data);
          if (hasSearched) {
            dispatch(userSigupAction.fetchUserPropertyData());
            dispatch(userSigupAction.selectedUserPropertyData({}));
          }
          dispatch(userSigupAction.clearPropertyQuestion());
          gotoQuestions(data);
          handleToggle();
        }}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          touched,
          errors,
          isSubmitting,
          handleSubmit,
          validateForm,
          validateField,
        }) => {
          return (
            <Form
              onSubmit={handleSubmit}
              id="signup-find-property-by-address-form"
            >
              {/* Hubspot hidden input for cookie breaker */}
              <HubspotCookieBreakerInput />

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label
                    htmlFor="street_number"
                    className="text-muted-foreground text-xs"
                  >
                    Street Number
                  </Label>
                  <FormInput
                    name="street_number"
                    id="streetNumber"
                    placeholder="Street Number"
                    validation={{
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                    }}
                    className="h-10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="site_route"
                    className="text-muted-foreground text-xs"
                  >
                    Street Name
                  </Label>
                  <FormInput
                    name="site_route"
                    id="streetName"
                    placeholder="Street Name"
                    validation={{
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                    }}
                    className="h-10 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="city"
                    className="text-muted-foreground text-xs"
                  >
                    City
                  </Label>
                  <FormInput
                    name="city"
                    id="property_city"
                    placeholder="City"
                    validation={{
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                    }}
                    className="h-10 text-sm"
                  />
                </div>
                <StateDropdown
                  stateList={USA_STATES || []}
                  value={values.state || ""}
                  onChange={(selectedValue) => {
                    setFieldValue("state", selectedValue, true);
                    setFieldTouched("state", true, false);
                    requestAnimationFrame(() => {
                      validateField("state");
                    });
                  }}
                  onBlur={() => setFieldTouched("state", true, true)}
                  name="state"
                  label="State"
                  placeholder="Select state"
                  touched={touched.state}
                  error={errors.state}
                  labelClassName="text-muted-foreground text-xs"
                />
                <div className="space-y-1">
                  <Label
                    htmlFor="zip"
                    className="text-muted-foreground text-xs"
                  >
                    Zip
                  </Label>
                  <FormInput
                    name="zip"
                    id="property_zip"
                    placeholder="Zip"
                    validation={{
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                    }}
                    className="h-10 text-sm"
                  />
                </div>
                <div className="col-span-2 min-[768px]:col-span-1 flex flex-row items-start gap-3">
                  <div className="space-y-1 w-full min-[768px]:w-24 min-[768px]:shrink-0">
                    <Label
                      htmlFor="unit_no"
                      className="text-muted-foreground text-xs"
                    >
                      Unit
                    </Label>
                    <FormInput
                      name="unit_no"
                      id="unit_number"
                      placeholder="Unit"
                      validation={{
                        values,
                        setFieldValue,
                        setFieldTouched,
                        touched,
                        errors,
                      }}
                      className="h-10 text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 min-[768px]:mt-6 min-[768px]:flex-1 min-w-0">
                    Please include your unit or condominium number when
                    submitting your information so we can accurately locate and
                    verify your property.
                  </p>
                </div>
              </div>

              {/* Agreement Checkbox — required before search (Complete Protection only) */}
              {isCompleteProtection && (
                <>
                  <div className="flex items-start gap-2 mt-6 mb-6">
                    <Checkbox
                      id="agreement"
                      checked={agreeToTerms}
                      onCheckedChange={(checked) => {
                        setAgreeToTerms(checked as boolean);
                        if (checked) {
                          setTermsError("");
                        }
                      }}
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="agreement"
                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      For security reasons, I understand that I will be asked
                      challenge questions and/or my customer service PIN to
                      verify my identity. If I receive a call and I am NOT asked
                      the proper challenge questions, I will hang up and call
                      Customer Support immediately.
                    </Label>
                  </div>
                  {termsError && (
                    <p className="text-xs text-red-500 mb-4 -mt-4">
                      {termsError}
                    </p>
                  )}
                </>
              )}

              {showBypassCheckbox ? (
                <div className="flex items-start gap-2 mb-6">
                  <Checkbox
                    id="bypass-security-questions-address"
                    checked={Boolean(isQuestionByPass)}
                    onCheckedChange={(checked) => {
                      dispatch(
                        propertiesAction.setIsQuestionByPass(checked === true),
                      );
                    }}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="bypass-security-questions-address"
                    className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                  >
                    Do you want to bypass security questions for this property?
                    {isAdminCreateUser
                      ? " Use this when ownership was verified another way."
                      : ""}
                  </Label>
                </div>
              ) : null}

              {/* Next Step Button */}
              <div
                className={`flex justify-center mt-6 ${
                  propertyQuestion?.success ||
                  (isMonitoringCategory && isPropertyConfirmed)
                    ? "gap-4"
                    : ""
                }`}
              >
                <Button
                  type="submit"
                  // disabled={!isFormValid || isSubmitting}
                  onClick={async (e) => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    const formErrors = await validateForm();
                    const firstErrorPath = scrollToFirstFormikError({
                      errors: formErrors,
                      setFieldTouched,
                    });
                    if (firstErrorPath) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS + " mb-2"}
                >
                  SEARCH {hasSearched ? "AGAIN" : ""}
                </Button>
                {(propertyQuestion?.success ||
                  (isMonitoringCategory && isPropertyConfirmed)) && (
                  <Button
                    type="button"
                    disabled={!isPropertyConfirmed}
                    className={`font-medium px-10 py-2.5 text-sm rounded-full shadow-md transition-all
                      bg-secondary bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 
                      hover:to-cyan-400/90 text-white hover:shadow-lg ring-2 ring-secondary/30 ring-offset-2   
                  `}
                    onClick={goToNextStep}
                  >
                    NEXT
                  </Button>
                )}
              </div>
              {(addPropertyError || questionsErrMsg || errMsg) && (
                <div className="w-full rounded-xl border border-red-200 bg-red-50 p-3 mt-2">
                  <PropertySearchErrorMessage
                    message={addPropertyError || questionsErrMsg || errMsg}
                    className="text-center text-red-800 text-sm"
                    linkClassName="text-secondary hover:underline font-medium"
                  />
                </div>
              )}
            </Form>
          );
        }}
      </Formik>

      {/* Address Question Modal */}
      <Dialog
        open={showAddressModal && !(isFromRiskReport && bindRiskReportLoader)}
        onOpenChange={setShowAddressModal}
      >
        <DialogContent className="sm:max-w-md p-0 gap-0 border-0 overflow-hidden">
          <DialogTitle className="sr-only">Attention</DialogTitle>
          <div className="p-8 text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-secondary/20 to-cyan-400/20 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary/30 to-cyan-400/30 flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-secondary" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-semibold text-foreground mb-3">
              Attention
            </h2>

            {/* Message */}
            <p className="text-muted-foreground mb-8">
              Would you like to use the address you've provided with the
              account?
            </p>

            {/* Buttons */}
            <div className="flex gap-4 justify-center">
              <Button
                onClick={handleEnterNewAddress}
                variant="outline"
                className="px-8 py-2.5 rounded-full font-medium"
              >
                NOPE
              </Button>
              <Button
                onClick={handleUseExistingAddress}
                className="bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90 text-white font-medium px-8 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
              >
                YES
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-border py-4 px-6 text-center bg-muted/30">
            <p className="text-muted-foreground text-sm flex items-center justify-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Having trouble?{" "}
              <a
                href="#"
                className="text-secondary hover:text-secondary/80 font-medium"
              >
                Contact support
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FindPropertyByAddressV2;
